import React, { useState, Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, TruckIcon, ShieldCheckIcon, RotateCcwIcon, MapPinIcon, PhoneIcon, SendIcon, MailIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/product/ProductCard';
import { Input } from '../components/ui/Input';
import { categories } from '../data/categories';
import { products } from '../data/products';
import { blogPosts, faqItems } from '../data/blog';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function HomePage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const bestSellers = products.filter(p => p.badge === 'Хіт продажів').slice(0, 4);
  const newProducts = products.filter(p => p.badge === 'Новинка').slice(0, 4);
  const saleProducts = products.filter(p => p.oldPrice).slice(0, 4);
  const handleConsultationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Дякуємо! Ми зв'яжемося з вами найближчим часом.");
    setConsultationForm({
      name: '',
      phone: '',
      email: ''
    });
  };
  return <main data-id="element-819">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-id="element-820">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-821">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-12 lg:py-20" data-id="element-822">
            {/* Left Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="order-2 lg:order-1 text-center lg:text-left" data-id="element-823">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4 lg:mb-6" data-id="element-824">
                Басейни та обладнання для{' '}
                <span className="text-sky-500" data-id="element-825">чистої води</span> та комфорту
              </h1>
              <h2 className="text-lg sm:text-xl text-slate-600 mb-6 lg:mb-8" data-id="element-826">
                Aqua Pools (Івано-Франківськ) — підбір, доставка та сервіс.
                Допоможемо зібрати комплект під ваш басейн.
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-827">
                <Link to="/catalog" className="w-full sm:w-auto" data-id="element-828">
                  <Button size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRightIcon className="w-5 h-5" data-id="element-830" />} data-id="element-829">
                    Перейти в каталог
                  </Button>
                </Link>
                <Link to="/contacts" className="w-full sm:w-auto" data-id="element-831">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-832">
                    Контакти
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Image - Updated with new pool image */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-1 lg:order-2" data-id="element-833">
              <div className="relative aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden shadow-2xl" data-id="element-834">
                <img src="https://cdn.magicpatterns.com/uploads/rADafJCbbtvX3awmsyEfK8/raimo-lantelankallio-PG9c6rWUpVM-unsplash.jpg" alt="Басейн" className="w-full h-full object-cover" data-id="element-835" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 lg:py-16 bg-slate-50" data-id="element-836">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-837">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={containerVariants} data-id="element-838">
            <div className="text-center mb-10 lg:mb-12" data-id="element-839">
              <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-id="element-840">
                Чому обирають нас?
              </motion.h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-841">
              <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-lg transition-shadow" data-id="element-842">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-4" data-id="element-843">
                  <TruckIcon className="w-7 h-7 text-sky-600" data-id="element-844" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" data-id="element-845">
                  Швидка доставка
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-846">
                  Відправляємо замовлення в день оформлення. Доставка по всій
                  Україні протягом 1-3 днів.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-lg transition-shadow" data-id="element-847">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-4" data-id="element-848">
                  <ShieldCheckIcon className="w-7 h-7 text-sky-600" data-id="element-849" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" data-id="element-850">
                  Гарантія на товар
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-851">
                  Офіційна гарантія виробника на все обладнання від 12 до 36
                  місяців. Якість підтверджена.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-lg transition-shadow" data-id="element-852">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-4" data-id="element-853">
                  <RotateCcwIcon className="w-7 h-7 text-sky-600" data-id="element-854" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" data-id="element-855">
                  Швидке повернення
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-856">
                  Повернення товару протягом 14 днів без зайвих питань. Ваш
                  комфорт — наш пріоритет.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-12 lg:py-16 bg-white" data-id="element-857">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-858">
          <div className="text-center mb-10 lg:mb-12" data-id="element-859">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-860">
              Хіт продажів
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-861">
            {bestSellers.map(product => <ProductCard key={product.id} product={product} data-id="element-862" />)}
          </div>
          <div className="text-center mt-8 lg:mt-10" data-id="element-863">
            <Link to="/catalog" data-id="element-864">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-866" />} data-id="element-865">
                Дивитись всі
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* New Products */}
      <section className="py-12 lg:py-16 bg-slate-50" data-id="element-867">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-868">
          <div className="text-center mb-10 lg:mb-12" data-id="element-869">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-870">
              Новинки
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-871">
            {newProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-872" />)}
          </div>
          <div className="text-center mt-8 lg:mt-10" data-id="element-873">
            <Link to="/catalog" data-id="element-874">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-876" />} data-id="element-875">
                Дивитись всі
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sale Products */}
      <section className="py-12 lg:py-16 bg-white" data-id="element-877">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-878">
          <div className="text-center mb-10 lg:mb-12" data-id="element-879">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-880">
              Розпродаж
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-881">
            {saleProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-882" />)}
          </div>
          <div className="text-center mt-8 lg:mt-10" data-id="element-883">
            <Link to="/catalog" data-id="element-884">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-886" />} data-id="element-885">
                Дивитись всі
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 lg:py-16 bg-slate-50" data-id="element-887">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-888">
          <div className="text-center mb-10 lg:mb-12" data-id="element-889">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-id="element-890">
              Категорії товарів
            </h2>
            <p className="text-lg text-slate-600" data-id="element-891">
              Все необхідне для басейну, сауни та відпочинку на воді
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-892">
            {categories.map(category => <div key={category.id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col h-full" data-id="element-893">
                <div className="aspect-[4/3] bg-slate-200 flex items-center justify-center" data-id="element-894">
                  <div className="text-center p-6" data-id="element-895">
                    <div className="w-16 h-16 bg-slate-300 rounded-full mx-auto mb-3" data-id="element-896"></div>
                    <p className="text-sm text-slate-500" data-id="element-897">
                      Зображення категорії
                    </p>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col" data-id="element-898">
                  <h3 className="font-semibold text-slate-900 mb-4 text-center flex-1 flex items-center justify-center min-h-[3rem]" data-id="element-899">
                    {category.name}
                  </h3>
                  <Link to={`/category/${category.slug}`} className="mt-auto" data-id="element-900">
                    <Button variant="outline" size="sm" className="w-full" data-id="element-901">
                      Переглянути
                    </Button>
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Map & Contacts */}
      <section className="py-12 lg:py-16 bg-white" data-id="element-902">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-903">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-904">
            {/* Map - Google Maps Embed for Ivano-Frankivsk center */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-80 lg:h-96" data-id="element-905">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2598.8947845935634!2d24.709722315743!3d48.92267997929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4730c16c34d5e0e3%3A0x4a0d9b0e4e4e4e4e!2z0IbQstCw0L3Qvi3QpNGA0LDQvdC60ZbQstGB0YzQuiwg0IbQstCw0L3Qvi3QpNGA0LDQvdC60ZbQstGB0YzQutCwINC-0LHQu9Cw0YHRgtGMLCDQo9C60YDQsNGX0L3QsA!5e0!3m2!1suk!2sua!4v1234567890123!5m2!1suk!2sua" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Карта Івано-Франківська" data-id="element-906"></iframe>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col justify-center" data-id="element-907">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-908">
                Контакти
              </h2>
              <div className="space-y-4" data-id="element-909">
                <a href="tel:+380671234567" className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-910">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-911">
                    <PhoneIcon className="w-6 h-6 text-sky-600" data-id="element-912" />
                  </div>
                  <div data-id="element-913">
                    <p className="text-sm text-slate-500" data-id="element-914">Телефон</p>
                    <p className="font-semibold text-slate-900" data-id="element-915">
                      +38 (067) 123-45-67
                    </p>
                  </div>
                </a>

                <a href="mailto:sales@aquapools.ua" className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-916">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-917">
                    <MailIcon className="w-6 h-6 text-sky-600" data-id="element-918" />
                  </div>
                  <div data-id="element-919">
                    <p className="text-sm text-slate-500" data-id="element-920">Email</p>
                    <p className="font-semibold text-slate-900" data-id="element-921">
                      sales@aquapools.ua
                    </p>
                  </div>
                </a>

                <a href="https://t.me/aquapools_if" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-922">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-923">
                    <SendIcon className="w-6 h-6 text-sky-600" data-id="element-924" />
                  </div>
                  <div data-id="element-925">
                    <p className="text-sm text-slate-500" data-id="element-926">Telegram</p>
                    <p className="font-semibold text-slate-900" data-id="element-927">
                      @aquapools_if
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-16 bg-slate-50" data-id="element-928">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-929">
          <div className="text-center mb-10 lg:mb-12" data-id="element-930">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-id="element-931">
              Часті питання
            </h2>
            <p className="text-lg text-slate-600" data-id="element-932">
              Відповіді на популярні питання наших клієнтів
            </p>
          </div>

          <div className="space-y-3" data-id="element-933">
            {faqItems.slice(0, 6).map(faq => <div key={faq.id} className="bg-white rounded-xl overflow-hidden shadow-sm" data-id="element-934">
                <button onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)} className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors" data-id="element-935">
                  <span className="font-medium text-slate-900 pr-4" data-id="element-936">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform ${openFaq === faq.id ? 'rotate-180' : ''}`} data-id="element-937" />
                </button>
                {openFaq === faq.id && <div className="px-5 pb-5 pt-0 border-t border-slate-100" data-id="element-938">
                    <p className="text-slate-600 pt-4" data-id="element-939">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>

          <div className="text-center mt-8" data-id="element-940">
            <Link to="/faq" data-id="element-941">
              <Button variant="outline" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-943" />} data-id="element-942">
                Всі питання
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="py-12 lg:py-16 bg-white" data-id="element-944">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-945">
          <div className="text-center mb-10 lg:mb-12" data-id="element-946">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-id="element-947">
              Блог та корисні поради
            </h2>
            <p className="text-lg text-slate-600" data-id="element-948">
              Ділимося досвідом догляду за басейном та вибору обладнання
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-949">
            {blogPosts.slice(0, 3).map(post => <article key={post.id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col" data-id="element-950">
                <Link to={`/blog/${post.id}`} className="block" data-id="element-951">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-200" data-id="element-952">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-953" />
                  </div>
                </Link>
                <div className="p-5 flex flex-col flex-1" data-id="element-954">
                  <div className="flex items-center gap-3 text-sm text-slate-500 mb-3" data-id="element-955">
                    <span data-id="element-956">{post.date}</span>
                    <span data-id="element-957">•</span>
                    <span data-id="element-958">{post.readTime}</span>
                  </div>
                  <Link to={`/blog/${post.id}`} data-id="element-959">
                    <h3 className="font-semibold text-slate-900 mb-2 hover:text-sky-600 transition-colors line-clamp-2" data-id="element-960">
                      {post.title}
                    </h3>
                  </Link>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4 flex-1" data-id="element-961">
                    {post.excerpt}
                  </p>
                  <Link to={`/blog/${post.id}`} className="mt-auto" data-id="element-962">
                    <Button variant="outline" size="sm" className="w-full" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-964" />} data-id="element-963">
                      Читати статтю
                    </Button>
                  </Link>
                </div>
              </article>)}
          </div>

          <div className="text-center mt-8 lg:mt-10" data-id="element-965">
            <Link to="/blog" data-id="element-966">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-968" />} data-id="element-967">
                Всі статті
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Consultation Form - Updated with light color scheme */}
      <section className="py-12 lg:py-16 bg-slate-50" data-id="element-969">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-970">
          <div className="text-center mb-8 lg:mb-10" data-id="element-971">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-id="element-972">
              Замовити консультацію
            </h2>
            <p className="text-lg text-slate-600" data-id="element-973">
              Залишіть свої дані — ми зв'яжемося з вами та допоможемо з вибором
            </p>
          </div>

          <form onSubmit={handleConsultationSubmit} className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-slate-200" data-id="element-974">
            <div className="space-y-4" data-id="element-975">
              <Input label="Ваше ім'я" value={consultationForm.name} onChange={e => setConsultationForm({
              ...consultationForm,
              name: e.target.value
            })} placeholder="Іван Петров" required data-id="element-976" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-977">
                <Input label="Телефон" type="tel" value={consultationForm.phone} onChange={e => setConsultationForm({
                ...consultationForm,
                phone: e.target.value
              })} placeholder="+38 (0__) ___-__-__" required data-id="element-978" />
                <Input label="Email" type="email" value={consultationForm.email} onChange={e => setConsultationForm({
                ...consultationForm,
                email: e.target.value
              })} placeholder="email@example.com" required data-id="element-979" />
              </div>
              <Button type="submit" size="lg" fullWidth data-id="element-980">
                Замовити консультацію
              </Button>
            </div>
          </form>
        </div>
      </section>
    </main>;
}